import { MAP_H, MAP_W, TILE, zoneAt, ZONES } from "../data/map.ts";
import { MAP_PINS, routeToPin, type MapRoute } from "../data/routeMap.ts";
import { routePlan } from "../data/routePlan.ts";
import { TRAINERS, trainerLock } from "../data/trainers.ts";
import { weekOf } from "../game/goals.ts";
import { enterAge } from "../art/craftFeel.ts";
import { UI, withA } from "../art/pal.ts";
import { fitText } from "../engine/font.ts";
import { easeOut, SCREEN_H, SCREEN_W } from "../engine/gfx.ts";
import type { Ctx } from "../game/ctx.ts";
import type { Scene } from "../game/scenes.ts";
import { backdrop, glass, hint } from "../ui/kit.ts";
import { getWorld, HPX, WPX } from "../world/world.ts";

/** World pixels per map pixel: the whole (frontier-wide) network fits across the top. */
const K = Math.max(WPX / 364, HPX / 120);
const MW = Math.round(WPX / K), MH = Math.round(HPX / K);

let mini: ImageData | null = null;
/** Painted minimap: the baked ground downsampled with forests, roofs and water accented. */
function minimap(): ImageData {
  if (mini) return mini;
  const world = getWorld();
  const img = new ImageData(MW, MH);
  const src = world.ground.data;
  for (let y = 0; y < MH; y++) {
    for (let x = 0; x < MW; x++) {
      let r = 0, g = 0, b = 0, n = 0;
      for (let sy = Math.floor(y * K); sy < Math.floor((y + 1) * K); sy++) {
        for (let sx = Math.floor(x * K); sx < Math.floor((x + 1) * K); sx++) {
          const o = (sy * WPX + sx) * 4;
          r += src[o]!; g += src[o + 1]!; b += src[o + 2]!; n++;
        }
      }
      const o = (y * MW + x) * 4;
      img.data[o] = r / n; img.data[o + 1] = g / n; img.data[o + 2] = b / n; img.data[o + 3] = 255;
    }
  }
  const dot = (wx: number, wy: number, rad: number, c: readonly number[]) => {
    const cx = wx / K, cy = wy / K;
    for (let y = Math.floor(cy - rad); y <= cy + rad; y++) for (let x = Math.floor(cx - rad); x <= cx + rad; x++) {
      if (x < 0 || y < 0 || x >= MW || y >= MH) continue;
      if ((x - cx) ** 2 + (y - cy) ** 2 > rad * rad) continue;
      const o = (y * MW + x) * 4;
      img.data[o] = c[0]!; img.data[o + 1] = c[1]!; img.data[o + 2] = c[2]!;
    }
  };
  for (const o of world.objects) {
    if (o.kind === "tree") dot(o.x, o.y - 10, 3, [52, 120, 64]);
    else if (o.kind === "pine") dot(o.x, o.y - 10, 2.6, [36, 92, 70]);
    else if (o.kind === "tall") dot(o.x, o.y - 6, 1.6, [60, 140, 70]);
    else if (o.kind === "building") {
      const b = o.building!;
      const x0 = (o.x - (b.tw * TILE) / 2) / K, x1 = (o.x + (b.tw * TILE) / 2) / K;
      const y0 = (o.y - b.th * TILE) / K, y1 = o.y / K;
      const roof = b.kind === "clinic" ? [224, 90, 90] : b.kind === "stall" ? [70, 190, 170] : [90, 130, 220];
      for (let y = Math.floor(y0); y < y1; y++) for (let x = Math.floor(x0); x < x1; x++) {
        if (x < 0 || y < 0 || x >= MW || y >= MH) continue;
        const oo = (y * MW + x) * 4;
        const edge = y >= y1 - 2;
        img.data[oo] = edge ? 240 : roof[0]!; img.data[oo + 1] = edge ? 226 : roof[1]!; img.data[oo + 2] = edge ? 200 : roof[2]!;
      }
    }
  }
  mini = img;
  return img;
}

export function routeMapScene(ctx: Ctx): Scene {
  let selected = 0, route: MapRoute | null = null, age = enterAge(ctx.shot);
  const refresh = () => { route = routeToPin(ctx.state, MAP_PINS[selected]!); };
  return {
    id: "map",
    native: true,
    enter() {
      selected = Math.max(0, MAP_PINS.findIndex(p => p.trainer === routePlan({ ...ctx.state, week: weekOf(ctx.today()) }).next.id));
      age = enterAge(ctx.shot);
      refresh();
    },
    exit() {},
    update() {
      age += ctx.dt;
      if (ctx.input.consume("Escape")) { ctx.audio.back(); ctx.pop(); return; }
      const d = ctx.input.menuDir(ctx.now);
      if (ctx.input.consume("Enter") || d?.x || d?.y) {
        selected = (selected + (d && (d.x < 0 || d.y < 0) ? -1 : 1) + MAP_PINS.length) % MAP_PINS.length;
        refresh(); ctx.audio.cursor();
      }
    },
    draw() {
      const g = ctx.g, pin = MAP_PINS[selected]!;
      const reduced = ctx.reduced;
      backdrop(g, ctx.now, reduced, [30, 44, 60, 255], [10, 12, 24, 255]);
      const k = reduced ? 1 : easeOut(Math.min(1, age / 240));
      const X = Math.round((SCREEN_W - MW) / 2), Y = 10 + Math.round((1 - k) * 10);
      glass(g, X - 4, Y - 4, MW + 8, MH + 8, { r: 8 });
      g.blit(minimap(), X, Y);
      const cell = TILE / K;
      const at = (tx: number, ty: number) => ({ x: X + (tx + 0.5) * cell, y: Y + (ty + 0.5) * cell });
      // Route.
      const tiles = route?.tiles ?? [];
      const dash = reduced ? 0 : Math.floor(ctx.now / 90);
      tiles.forEach((t, n) => {
        const p = at(t.x, t.y);
        if ((n + dash) % 3 !== 0) g.disc(p.x, p.y, 1.1, UI.goldHi);
      });
      // Pins.
      for (const p of MAP_PINS) {
        const q = at(p.x, p.y);
        const c = p.kind === "trainer" ? UI.pink : p.kind === "rest" ? UI.gold : UI.teal;
        g.disc(q.x, q.y, 2.6, UI.ink);
        g.disc(q.x, q.y, 1.8, c);
      }
      // You.
      const you = at(ctx.state.x, ctx.state.y);
      const pulse = reduced ? 0 : (ctx.now / 600) % 1;
      if (!reduced) g.ring(you.x, you.y, 3 + pulse * 7, 3 + pulse * 7, withA(UI.white, 220 * (1 - pulse)), 1.2);
      g.disc(you.x, you.y, 3, UI.ink);
      g.disc(you.x, you.y, 2.2, UI.white);
      g.disc(you.x, you.y, 1.2, UI.pink);
      // Selected pin highlight.
      const sp = at(pin.x, pin.y);
      g.ring(sp.x, sp.y, 5, 5, UI.goldHi, 1.5);
      g.text(sp.x - 2, sp.y - 14 - (reduced ? 0 : Math.round(Math.abs(Math.sin(ctx.now / 200)) * 2)), "▼", UI.goldHi);
      // Info card under the map.
      const iy = Y + MH + 10, ix = 6, iw = SCREEN_W - 12;
      glass(g, ix, iy, iw, SCREEN_H - iy - 6, { r: 8 });
      g.textShadow(ix + 10, iy + 6, "ROUTE MAP", UI.textDim, UI.ink);
      g.textR(ix + 170, iy + 6, `${selected + 1}/${MAP_PINS.length}`, UI.textDim);
      const locked = pin.trainer && trainerLock(TRAINERS[pin.trainer], { ...ctx.state, week: weekOf(ctx.today()) });
      const label = locked ? (ctx.state.defeatedTrainers.includes(pin.trainer!)
        ? TRAINERS[pin.trainer!].rematchPrize ? " / WAIT" : " / DONE" : " / LOCK") : "";
      g.rrect(ix + 6, iy + 18, 174, 16, 6, withA(pin.kind === "trainer" ? UI.pink : UI.teal, 50));
      g.textShadow(ix + 11, iy + 22, fitText(pin.name + label, 164), UI.goldHi, UI.ink);
      g.text(ix + 10, iy + 40, ZONES[zoneAt(pin.x, pin.y)].name, UI.textDim);
      const status = route ? `${route.tiles.length - 1} steps / ${route.grassSteps} grass steps` : "No walkable route. The mud says no.";
      g.para(ix + 10, iy + 52, status, 170, UI.text, 11, 2);
      // Legend.
      const legend: [readonly [number, number, number, number], string][] = [[UI.white, "YOU"], [UI.teal, "SERVICE"], [UI.gold, "KETTLE"], [UI.pink, "TRAINER"], [UI.goldHi, "ROUTE"]];
      legend.forEach(([c, s], n) => {
        const lx = ix + 196 + (n % 2) * 58, ly = iy + 8 + Math.floor(n / 2) * 12;
        g.disc(lx + 3, ly + 3, 3, c);
        g.text(lx + 10, ly, fitText(s, 44), UI.textDim);
      });
      hint(g, ix + 196, SCREEN_H - 24, "A", "next pin", "a");
      hint(g, ix + 290, SCREEN_H - 24, "B", "back", "b");
      void MAP_W; void MAP_H;
    },
  };
}
